import { InternalTeamMemberDTOTeamEnum } from '@reposit/api-client';
import { Formik, FormikProps } from 'formik';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as yup from 'yup';

import Button from '../Button';
import FieldWithLabel from '../FormFields/FieldWithLabel';
import Select from '../FormFields/Select';
import Loading from '../Loading';
import Modal from '../Modal/index';
import { Header3, P2 } from '../Typography';

import { getInternalTeamMembers } from '../../redux/internal-team-member/internal-team-member.selectors';
import { GET_INTERNAL_TEAM_MEMBERS_STORE_KEY } from '../../redux/internal-team-member/internal-team-member.actions';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  assignAccountManagerRequested,
  ASSIGN_ACCOUNT_MANAGER_STORE_KEY,
} from '../../redux/organization-internal-owner/organization-internal-owner.actions';

export const ACCOUNT_MANAGER_PLACEHOLDER_VALUE = 'PLEASE_SELECT_ACCOUNT_MANAGER';

const ActionBar = styled.div`
  display: flex;
  padding: 24px 0;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 10px;

  button {
    margin-right: 10px;
  }
`;

interface AssignAccountManagerModalProps {
  organizationId: string;
  currentInternalTeamMemberId?: string;
  onDismiss: () => void;
}

interface AssignAccountManagerForm {
  internalTeamMemberId: string;
}

const VALIDATION_SCHEMA = yup.object().shape({
  internalTeamMemberId: yup
    .string()
    .required('Required')
    .notOneOf([ACCOUNT_MANAGER_PLACEHOLDER_VALUE], 'Required'),
});

export const AssignAccountManagerModal: React.FC<AssignAccountManagerModalProps> = ({
  organizationId,
  currentInternalTeamMemberId,
  onDismiss,
}) => {
  const dispatch = useDispatch();
  const internalTeamMembers = useSelector(getInternalTeamMembers);
  const isLoading = useSelector(
    createLoadingSelector([ASSIGN_ACCOUNT_MANAGER_STORE_KEY, GET_INTERNAL_TEAM_MEMBERS_STORE_KEY])
  );

  const salesMembers = internalTeamMembers.filter(
    (member) => member && member.team === InternalTeamMemberDTOTeamEnum.SALES && member.displayName !== 'UNKNOWN'
  );

  const isChanging = Boolean(currentInternalTeamMemberId);

  return (
    <Modal onDismiss={onDismiss}>
      <Header3>{isChanging ? 'Change Account Manager' : 'Assign Account Manager'}</Header3>
      <P2>Select a sales team member to act as the account manager for this organisation.</P2>

      <Formik
        validationSchema={VALIDATION_SCHEMA}
        initialValues={{
          internalTeamMemberId: currentInternalTeamMemberId || ACCOUNT_MANAGER_PLACEHOLDER_VALUE,
        }}
        onSubmit={(values) => {
          dispatch(assignAccountManagerRequested(organizationId, values.internalTeamMemberId));
          onDismiss();
        }}
      >
        {({ values, handleSubmit, isValid, setFieldValue }: FormikProps<AssignAccountManagerForm>) => {
          if (isLoading) {
            return <Loading />;
          }
          return (
            <form onSubmit={handleSubmit}>
              <FieldWithLabel label="Account manager">
                <Select
                  name="internalTeamMemberId"
                  value={values.internalTeamMemberId}
                  onChange={(value: string) => setFieldValue('internalTeamMemberId', value)}
                >
                  <option value={ACCOUNT_MANAGER_PLACEHOLDER_VALUE}>Please select an account manager</option>
                  {salesMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.displayName}
                    </option>
                  ))}
                </Select>
              </FieldWithLabel>
              <ActionBar>
                <Button buttonType="warning" type="button" onClick={onDismiss}>
                  Cancel
                </Button>
                <Button disabled={!isValid}>Confirm</Button>
              </ActionBar>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AssignAccountManagerModal;
